// src/components/Cart.js
import React, { useState, useEffect, useRef } from 'react';

const Cart = ({ 
  isOpen, 
  onClose, 
  primaryColor,
  language = 'en',
  items = [],
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  contacts = [],
  username = '',
  storeUrl = '' // Add storeUrl prop
}) => {
  const [slideDirection, setSlideDirection] = useState('right');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showContactError, setShowContactError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const cartRef = useRef(null);

  // Translations
  const translations = {
    en: {
      cart: 'Your Cart',
      emptyCart: 'Your cart is empty',
      emptyCartMessage: 'Start shopping to add items to your cart',
      continueShopping: 'Continue Shopping',
      total: 'Total',
      checkout: 'Checkout via Telegram',
      remove: 'Remove',
      quantity: 'Qty',
      subtotal: 'Subtotal',
      estimatedTotal: 'Total',
      orderSummary: 'Order Summary',
      items: 'Items',
      thankYou: 'Thank you for your order!',
      orderDetails: 'Order Details',
      customerInfo: 'Customer Information',
      totalItems: 'Total Items',
      noTelegramContact: 'No Telegram contact available. Please contact the store directly.',
      contactStore: 'Contact Store',
      ok: 'OK',
      noContacts: 'No contact information available. Please contact the store directly.',
      telegramNotAvailable: 'Telegram contact not found. Please use other contact methods.',
      storeLink: 'Store Link'
    },
    km: {
      cart: 'កន្ត្រករបស់អ្នក',
      emptyCart: 'កន្ត្រករបស់អ្នកទទេ',
      emptyCartMessage: 'ចាប់ផ្តើមទិញទំនិញដើម្បីបន្ថែមទំនិញទៅកន្ត្រក',
      continueShopping: 'បន្តការទិញទំនិញ',
      total: 'សរុប',
      checkout: 'ចេញវិក្កយបត្រតាម Telegram',
      remove: 'ដកចេញ',
      quantity: 'ចំនួន',
      subtotal: 'សរុបរង',
      estimatedTotal: 'សរុប',
      orderSummary: 'សង្ខេបការបញ្ជាទិញ',
      items: 'ផលិតផល',
      thankYou: 'សូមអរគុណចំពោះការបញ្ជាទិញរបស់អ្នក!',
      orderDetails: 'ព័ត៌មានលម្អិតនៃការបញ្ជាទិញ',
      customerInfo: 'ព័ត៌មានអតិថិជន',
      totalItems: 'ផលិតផលសរុប',
      noTelegramContact: 'មិនមានទំនាក់ទំនង Telegram សម្រាប់បញ្ជាទិញ។ សូមទាក់ទងហាងដោយផ្ទាល់។',
      contactStore: 'ទាក់ទងហាង',
      ok: 'យល់ព្រម',
      noContacts: 'មិនមានព័ត៌មានទំនាក់ទំនង។ សូមទាក់ទងហាងដោយផ្ទាល់។',
      telegramNotAvailable: 'រកមិនឃើញទំនាក់ទំនង Telegram ។ សូមប្រើបណ្តាញទំនាក់ទំនងផ្សេងទៀត។',
      storeLink: 'តំណហាង'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  // Handle slide animation on open/close
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setSlideDirection('right');
      document.body.style.overflow = 'hidden';
    } else {
      setSlideDirection('left');
      setTimeout(() => {
        setIsAnimating(false);
        document.body.style.overflow = 'unset';
      }, 300);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Calculate totals
  const calculateSubtotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  const subtotal = calculateSubtotal();
  const total = calculateTotal();

  // Get Telegram contact
  const getTelegramContact = () => {
    console.log('Contacts received in Cart:', contacts);
    
    if (!contacts || contacts.length === 0) {
      console.log('No contacts found');
      return null;
    }
    
    // Try to find Telegram contact
    const telegram = contacts.find(c => c.platform?.toLowerCase() === 'telegram');
    if (telegram) {
      console.log('Telegram contact found:', telegram);
      return telegram;
    }
    
    // Try to find WhatsApp as fallback
    const whatsapp = contacts.find(c => c.platform?.toLowerCase() === 'whatsapp');
    if (whatsapp) {
      console.log('WhatsApp contact found as fallback:', whatsapp);
      return whatsapp;
    }
    
    // Return first contact as fallback
    console.log('Using first contact as fallback:', contacts[0]);
    return contacts[0];
  };

  // Get current store URL
  const getStoreUrl = () => {
    if (storeUrl) return storeUrl;
    // Fallback to current window location
    return window.location.href;
  };

  // Format order message for Telegram with store link
  const formatOrderMessage = () => {
    const date = new Date().toLocaleString();
    const orderId = Math.floor(Math.random() * 1000000);
    const storeName = username || 'Store';
    const storeLink = getStoreUrl();
    
    let message = `🛍️ *${t.orderSummary}*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `📋 *${t.orderDetails}*\n`;
    message += `📅 ${date}\n`;
    message += `🔢 Order #${orderId}\n`;
    message += `🏪 ${storeName}\n`;
    message += `🔗 ${t.storeLink}: ${storeLink}\n\n`;
    
    message += `📦 *${t.items}*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    
    items.forEach((item, index) => {
      const itemTotal = item.price * item.quantity;
      message += `${index + 1}. ${item.title}\n`;
      message += `   ${t.quantity}: ${item.quantity} × $${item.price.toFixed(2)}\n`;
      message += `   → $${itemTotal.toFixed(2)}\n\n`;
    });
    
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `💰 *${t.subtotal}*: $${subtotal.toFixed(2)}\n`;
    message += `💵 *${t.total}*: $${total.toFixed(2)}\n\n`;
    
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `👤 *${t.customerInfo}*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `📱 ${t.totalItems}: ${items.reduce((sum, item) => sum + item.quantity, 0)}\n`;
    message += `📦 ${t.items}: ${items.length}\n\n`;
    
    message += `🔗 ${t.storeLink}: ${storeLink}\n\n`;
    message += `🙏 ${t.thankYou}\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━`;
    
    return encodeURIComponent(message);
  };

  // Handle Telegram checkout
  const handleTelegramCheckout = () => {
    const telegramContact = getTelegramContact();
    
    if (!telegramContact) {
      setErrorMessage(t.noTelegramContact);
      setShowContactError(true);
      return;
    }

    const message = formatOrderMessage();
    let telegramLink = telegramContact.link || '';
    
    // Clean the link
    telegramLink = telegramLink.trim();
    
    // Extract username from various Telegram link formats
    let username = '';
    
    if (telegramLink.includes('t.me/')) {
      username = telegramLink.split('t.me/')[1]?.split('?')[0] || '';
    } else if (telegramLink.includes('telegram.me/')) {
      username = telegramLink.split('telegram.me/')[1]?.split('?')[0] || '';
    } else if (telegramLink.startsWith('@')) {
      username = telegramLink.substring(1);
    } else if (telegramLink.startsWith('+')) {
      // It's a phone number, keep as is
      username = telegramLink;
    } else if (telegramLink.match(/^[0-9]+$/)) {
      // It's a numeric phone number
      username = telegramLink;
    } else {
      // Assume it's a username
      username = telegramLink;
    }
    
    // Clean username
    username = username.replace(/^@/, '').split('?')[0];
    
    let finalLink;
    if (username.startsWith('+') || username.match(/^[0-9]+$/)) {
      // Phone number - use telegram link with phone
      finalLink = `https://t.me/${username}?text=${message}`;
    } else {
      // Username
      finalLink = `https://t.me/${username}?text=${message}`;
    }
    
    console.log('Opening Telegram link:', finalLink);
    
    // Open Telegram
    window.open(finalLink, '_blank', 'noopener noreferrer');
    
    // Close cart
    if (onClose) {
      onClose();
    }
    
    // Call onCheckout if provided
    if (onCheckout) {
      onCheckout(items, total);
    }
  };

  // Handle contact store (fallback)
  const handleContactStore = () => {
    setShowContactError(false);
    // Try to open first contact link
    if (contacts && contacts.length > 0) {
      window.open(contacts[0].link, '_blank', 'noopener noreferrer');
    }
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-50 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Cart Panel */}
      <div 
        ref={cartRef}
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-50 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ fontFamily: getFontFamily() }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 p-4 border-b border-gray-100 bg-white"
          style={{ borderBottomColor: `${primaryColor}20` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <i className="fas fa-shopping-cart" style={{ color: primaryColor }}></i>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{t.cart}</h2>
                <p className="text-xs text-gray-500">{items.length} {items.length === 1 ? 'item' : 'items'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
            >
              <i className="fas fa-times text-gray-500"></i>
            </button>
          </div>
        </div>

        {/* Cart Content */}
        <div className="flex flex-col h-[calc(100vh-180px)] overflow-y-auto p-4">
          {items.length === 0 ? (
            // Empty Cart State
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <i className="fas fa-shopping-bag text-gray-300 text-4xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">{t.emptyCart}</h3>
              <p className="text-sm text-gray-400 max-w-xs">{t.emptyCartMessage}</p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2.5 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md"
                style={{ backgroundColor: primaryColor }}
              >
                {t.continueShopping}
              </button>
            </div>
          ) : (
            // Cart Items List
            <div className="space-y-4">
              {items.map((item, index) => (
                <div 
                  key={item.id || index}
                  className="flex gap-4 p-3 bg-gray-50 rounded-xl hover:shadow-sm transition-shadow"
                >
                  {/* Product Image */}
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200">
                    <img 
                      src={item.image || 'https://via.placeholder.com/80x80?text=Product'} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                      }}
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-800 truncate">
                      {item.title}
                    </h4>
                    <p className="text-sm font-semibold" style={{ color: primaryColor }}>
                      ${item.price.toFixed(2)}
                    </p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                        className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors flex items-center justify-center text-xs font-bold"
                      >
                        <i className="fas fa-minus"></i>
                      </button>
                      <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors flex items-center justify-center text-xs font-bold"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => onRemoveItem && onRemoveItem(item.id)}
                    className="self-start text-gray-400 hover:text-red-500 transition-colors p-1"
                    aria-label={t.remove}
                  >
                    <i className="fas fa-trash-alt text-sm"></i>
                  </button>
                </div>
              ))}

              {/* Order Summary - No Tax, No Shipping */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t.subtotal}</span>
                    <span className="font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                    <span>{t.estimatedTotal}</span>
                    <span style={{ color: primaryColor }}>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Checkout Button */}
        {items.length > 0 && (
          <div className="sticky bottom-0 p-4 bg-white border-t border-gray-100">
            <button
              onClick={handleTelegramCheckout}
              className="w-full py-3 text-white font-semibold rounded-xl transition-all duration-200 hover:opacity-90 hover:shadow-lg flex items-center justify-center gap-3"
              style={{ backgroundColor: primaryColor }}
            >
              <i className="fab fa-telegram-plane"></i>
              <span>{t.checkout}</span>
              <span className="text-sm opacity-80">${total.toFixed(2)}</span>
            </button>
          </div>
        )}
      </div>

      {/* Contact Error Modal */}
      {showContactError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-2xl p-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-circle text-red-500 text-3xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {language === 'km' ? 'មិនមានទំនាក់ទំនង' : 'No Contact Available'}
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                {errorMessage || t.noTelegramContact}
              </p>
              <div className="flex gap-3">
                {contacts && contacts.length > 0 && (
                  <button
                    onClick={handleContactStore}
                    className="flex-1 py-2.5 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {t.contactStore}
                  </button>
                )}
                <button
                  onClick={() => setShowContactError(false)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t.ok}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Cart;